import React, { useCallback, useEffect, useState } from 'react'
import styles from './login.module.css'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFormik } from "formik"
import * as yup from "yup";
import { Checkbox, CircularProgress } from '@mui/material';
import { ArrowIcon, GoogleIcon, HidePasswordIcon, LogoIcon, PasswordVisible } from '../../../svg';
import { useDispatch } from 'react-redux'
import { login, loginWithGoogle } from '../../redux/loginSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import {
    signInWithPopup,
    GoogleAuthProvider,
} from "firebase/auth";
import { auth } from '../../helper/firebase';
import ReCAPTCHA from "react-google-recaptcha";
import Toastify from '../../helper/Toastify';
import api from '../../helper/Api';

const Login = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [error, setError] = useState(null);
    const [googleErrorMessage, setGoogleErrorMessage] = useState("");
    const [captchaValue, setCaptchaValue] = useState(null);
    const location = useLocation();
    const callbackUrl = location.search.split("callbackUrl=")[1] || null


    // schema -----------
    const schema = yup.object().shape({
        email: yup.string().matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Please enter valid email").required("Please enter valid email"),
        password: yup.string().required("Password is required")
    })

    const {
        errors, values, handleChange, touched, setFieldValue, handleBlur, resetForm, handleSubmit,
    } = useFormik({
        initialValues: {
            email: "",
            password: ""
        },
        validationSchema: schema,
        onSubmit: async (values) => {

            if (!captchaValue) {
                Toastify.error("Please verify the reCAPTCHA")
                return;
            }

            try {

                const { status } = await api.verifyCaptchaToken({ token: captchaValue })

                if (status === 200) {
                    handleLogin(values);
                } else {
                    Toastify.error("Invalid request")
                }
            } catch (err) {
                Toastify.error("Invalid request")
            }
        }
    })

    const handleCaptchaChange = (value) => {
        setCaptchaValue(value);
    };

    const handleLogin = useCallback(async (values) => {
        setLoading(true);
        try {
            const resultAction = await dispatch(login(values));
            const result = unwrapResult(resultAction);

            // If OTP is required, go to OTP verification page
            if (result?.pending) {
                navigate('/loginOtpVerification', {
                    state: { email: result.email, callbackUrl: callbackUrl || null },
                });
                return;
            }

            if (callbackUrl) {
                navigate(callbackUrl);
            } else {
                navigate('/courses/Courses');
            }
        } catch (error) {
            console.error('Login failed:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, navigate]);

    const handleGoogleSignUp = async (e) => {
        e.preventDefault();

        // Instantiate a GoogleAuthProvider object
        const provider = new GoogleAuthProvider();

        try {
            // Sign in with a pop-up window
            const result = await signInWithPopup(auth, provider);

            // Pull signed-in user credential.
            const user = result.user;

            const resultAction = await dispatch(loginWithGoogle({
                email: user.email,
                googleIdToken: user.accessToken
            }));

            unwrapResult(resultAction);

            if (callbackUrl) {
                navigate(callbackUrl)
            } else {
                navigate('/courses/Courses');
            }

        } catch (err) {
            // Handle errors here.
            const errorMessage = err.message;
            const errorCode = err.code;

            setError(true);

            switch (errorCode) {
                case "auth/operation-not-allowed":
                    setGoogleErrorMessage("Email/password accounts are not enabled.");
                    break;
                case "auth/operation-not-supported-in-this-environment":
                    setGoogleErrorMessage("HTTP protocol is not supported. Please use HTTPS.")
                    break;
                case "auth/popup-blocked":
                    setGoogleErrorMessage("Popup has been blocked by the browser. Please allow popups for this website.")
                    break;
                case "auth/popup-closed-by-user":
                    setGoogleErrorMessage("Popup has been closed by the user before finalizing the operation. Please try again.")
                    break;
                default:
                    setGoogleErrorMessage(errorMessage);
                    break;
            }
        }
    };


    return (
        <div className={styles.main}>
            <div className={styles.mainContainer}>
                <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
                    <LogoIcon />
                </div>
            </div>
            <div className={styles.login}>
                <div className={styles.mobileViewLogin}>
                    <div className={styles.mobileLogoStyle}>
                        <h3>KINGSTER</h3>
                    </div>
                </div>
                <div className={styles.content} style={{ marginTop: 30 }}>
                    <h3>Sign in to your account</h3>
                    {/* <p>Please login to continue to your account.</p> */}
                    <div style={{ marginTop: 40 }}>
                        <label className={styles.label}>Email</label>
                        <div className={styles.inputbox}>
                            <input
                                type='email'
                                onBlur={handleBlur}
                                value={values.email}
                                placeholder='Email Address'
                                name="email"
                                onChange={handleChange}
                            />
                        </div>
                        {
                            errors.email && touched.email && <p style={{ color: "red", fontSize: "12px", textAlign: "left" }}>{errors.email}</p>
                        }
                    </div>

                    <div style={{ marginTop: 20 }}>
                        <label className={styles.label}>Password</label>
                        <div className={styles.inputbox}>
                            <input
                                type={showNewPassword ? 'text' : 'password'}
                                onBlur={handleBlur}
                                value={values.password}
                                placeholder='Password'
                                name="password"
                                onChange={handleChange}
                            />
                            <div onClick={() => setShowNewPassword(!showNewPassword)} style={{ display: 'flex', alignItems: 'center', justifyContent: "center" }}>
                                {showNewPassword ? <PasswordVisible /> : <HidePasswordIcon />}
                            </div>
                        </div>
                        {
                            errors.password && touched.password && <p style={{ color: "red", fontSize: "12px", textAlign: "left" }}>{errors.password}</p>
                        }

                    </div>

                    <div style={{ transform: "scale(0.7)", transformOrigin: "0 0", marginTop: "10px" }}>
                        <ReCAPTCHA
                            sitekey="6Lc8vBcrAAAAANcniDVhDJlYjI_dim7gFk3L4u19"
                            onChange={handleCaptchaChange}
                            style={{ height: "40px" }}
                        />
                    </div>
                    <div
                        className={styles.forgotText}
                        onClick={() => navigate('/forgotPassword')}
                    >Forgot Password?</div>
                    <div className={styles.checkStyle} style={{ marginTop: 20 }}>
                        <div style={{ marginLeft: -10, }}>
                            <div className={styles.checkbox}>
                                <Checkbox /><span className={styles.agreetext}>Remember me</span>
                            </div>
                        </div>
                        <div
                            className={styles.saveButtonStyle}
                            onClick={handleSubmit}
                            disabled={loading}
                        // style={{width:'50%'}}
                        >
                            {
                                loading ?
                                    <CircularProgress size={25} thickness={4.5} sx={{ color: "#fff" }} />
                                    : "Sign In"
                            } <ArrowIcon />
                        </div>
                    </div>

                    <div style={{ marginTop: 20 }}>
                        <div >
                            <div className={styles.signWith} style={{ marginTop: 20 }}>
                                <div className={styles.lineStyle} />
                                <div className={styles.orText}>
                                    SIGN IN WITH
                                </div>
                                <div className={styles.lineStyle} />
                            </div>
                            <div className={styles.google}>
                                <div className={styles.googleStyle} onClick={handleGoogleSignUp}>
                                    <GoogleIcon /> <div className={styles.singleLine} />Google
                                </div>
                            </div>
                        </div>
                        <div className={styles.textStyle} onClick={() => navigate('/registration')}>
                            New at Kingster? <span className={styles.loginText}>Register here </span>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}



export default Login;
